import { isValidElement, useCallback, useMemo, useState } from "react";
import { useShallowEffect } from "@mantine/hooks";
import type { FormikProps, FormikValues } from "formik";
import type { ISteppedFlowFormik, IStepSchema, IUseSteppedFlowFormikOptions } from "../stepped-flow.types";
import { clampStepIndex, getFormikSteps, isStepSchema } from "../stepped-flow.utilities";

/**
 * The step and validation state behind a Formik stepped flow, independent of the shell (modal or drawer) it
 * is rendered in. Each step declares its own schema; Next is gated on the active step's schema alone, and
 * submit on the whole-form schema composed from every step's. Pass `formState` to `SteppedFlowFormikForm`
 * inside the shell's body, and drive the shell's header and footer from the rest.
 * @param options.active    The controlled active step index.
 * @param options.children  The shell's children, of which the SteppedFlowFormikStep elements are the steps.
 * @param options.stepName  How callers write the shell's step, used in dev-mode errors.
 */
export const useSteppedFlowFormik = <Values extends FormikValues = FormikValues>({
    active,
    children,
    stepName,
}: IUseSteppedFlowFormikOptions): ISteppedFlowFormik<Values> => {
    const [formik, setFormik] = useState<FormikProps<Values> | null>(null);

    // Memoised because the shell re-renders from this hook's `formik` state on every Formik change (each
    // keystroke) while `children` stays referentially stable, so the memo genuinely skips re-parsing the
    // children on those renders.
    const steps = useMemo(() => getFormikSteps(children), [children]);
    const clampedActive = clampStepIndex(active, steps.length);
    const isFinalStep = clampedActive === steps.length - 1;
    const isSubmitting = formik?.isSubmitting ?? false;
    const isValid = formik?.isValid ?? false;

    const [registeredSchemas, setRegisteredSchemas] = useState<Record<number, IStepSchema | undefined>>({});
    // Bail out when nothing changed: a slot re-registering the same schema must not loop through state.
    const registerValidation = useCallback((index: number, schema: IStepSchema | undefined) => {
        setRegisteredSchemas((previous) =>
            index in previous && previous[index] === schema ? previous : { ...previous, [index]: schema }
        );
    }, []);

    // One schema per step: the prop's when it is static, the slot's registration when it is an element.
    // `validation` is typed as required, but the type system behind that guarantee never actually runs
    // here (no typecheck target, no `tsc` build, esbuild/swc strip types at transpile time) — so an
    // untyped or careless caller CAN reach this at runtime with `validation` omitted or malformed. Guard
    // structurally rather than trusting the cast: a value that is neither a valid element nor a real
    // schema becomes `undefined` (fails closed, see `isValidationReady`/`isActiveStepValid` below)
    // instead of being passed through to crash later on `.isValidSync` (e.g. `validation={null}`) or on
    // `.concat` while composing the whole-form schema (a half-schema carrying only `isValidSync`).
    const stepSchemas = useMemo(
        () =>
            steps.map((step, index): IStepSchema | undefined => {
                const { validation } = step.props;
                if (isValidElement(validation)) {
                    return registeredSchemas[index];
                }
                if (isStepSchema(validation)) {
                    return validation;
                }
                if (process.env.NODE_ENV !== "production") {
                    console.error(
                        `${stepName} "${step.props.label}" (index ${index}) has an invalid ` +
                            "`validation` prop: expected a Yup-like schema or a registering element. Next and " +
                            "submit will stay disabled for this step until it declares one."
                    );
                }
                return undefined;
            }),
        [steps, registeredSchemas, stepName]
    );

    // The whole-form schema IS the steps' schemas, so per-step gating and submit gating cannot disagree.
    // No seed: an empty reduce would need a schema constant, which would mean depending on yup here.
    // `undefined` is a valid Formik validationSchema, and the readiness gate below stops that state being
    // actionable.
    const validationSchema = useMemo(() => {
        const declared = stepSchemas.filter((schema): schema is IStepSchema => typeof schema !== "undefined");
        return declared.length === 0 ? undefined : declared.reduce((accumulated, schema) => accumulated.concat(schema));
    }, [stepSchemas]);

    const values = formik?.values;
    const activeStepSchema = stepSchemas[clampedActive];
    // A later step's errors must not block progress through the current one. Validating the whole values
    // object against one step's schema works because unknown keys are allowed, and `.when()` referring to
    // another step's field still resolves against the parent value object.
    //
    // `activeStepSchema` is undefined for two different reasons, which must NOT be treated alike.
    // When there are no steps at all there is no active step to speak of, so fall back to the whole
    // form's `isValid` for that harmless edge case (and the shell suppresses its Next button entirely
    // then anyway). Every other case — a registering element that has not reported yet, a static
    // `validation` left `undefined` by an unchecked caller, or any value that failed the runtime schema
    // check above — must block Next outright rather than loosen to whichever way the whole form happens
    // to read. `validation` being a required prop does NOT make "nobody declared this step's rules"
    // unreachable at runtime: this repo has no typecheck target, `tsc -p` cannot run at all, and both CI
    // (esbuild) and Jest (`@swc/jest`) transpile without ever checking types, so a step that forgot
    // `validation` reaches here at runtime — and falling back to `isValid` here is what reproduced the
    // original dead-end.
    const isActiveStepValid = useMemo(() => {
        if (typeof activeStepSchema !== "undefined") {
            return activeStepSchema.isValidSync(values ?? {});
        }
        return steps.length === 0 ? isValid : false;
    }, [activeStepSchema, isValid, values, steps.length]);

    // Formik's validate callback is a useEventCallback — useCallback(fn, []) — so its mount-validation
    // effect never re-runs and nothing re-validates when validationSchema changes identity. Do it here so
    // no caller has to remember.
    //
    // Depends on the steps' schemas, compared shallowly, rather than on the composed `validationSchema`:
    // that object is rebuilt whenever `stepSchemas` is, and `stepSchemas` derives from `children`, which
    // are fresh elements on every render of the caller — so its identity changed on every step change
    // even though no step's rules had. Re-validating there was actively harmful, not just wasted work:
    // `validateForm()` validates whatever values Formik holds when it is called, and an arriving step's
    // own mount effect can update a value in the same commit (the model form's Sensitivity step prefills
    // its threshold from the resolved floor). Child effects run before the parent's, so this call was
    // issued last, resolved last, and its pre-update result overwrote the fresher one — leaving submit
    // disabled on an already-valid form until a blur re-validated it.
    const validateForm = formik?.validateForm;
    useShallowEffect(() => {
        void validateForm?.();
    }, [validateForm, stepSchemas]);

    // Registration lands in an effect, so on the first frame nothing has registered and an incomplete
    // composed schema would read as a valid form. Submit validates the WHOLE form, so it must stay
    // disabled until every step has a schema — unlike Next, which only ever needs the active step's own
    // readiness (folded into `isActiveStepValid` above) because a later step's rules must never block
    // progress through an earlier one. Checking every step (not just the ones known to register via a
    // slot) also covers a step whose `stepSchemas` entry is `undefined` for any other reason — a static
    // `validation` left undefined, or an element that never calls `useStepValidation` — so submit stays
    // blocked rather than reading an incomplete composed schema as valid.
    const isValidationReady = stepSchemas.every((schema) => typeof schema !== "undefined");

    return {
        steps,
        active: clampedActive,
        isFinalStep,
        formik,
        isSubmitting,
        isValid,
        isActiveStepValid,
        isValidationReady,
        formState: {
            steps,
            active: clampedActive,
            validationSchema,
            registerValidation,
            onFormikChange: setFormik,
        },
    };
};
